import { MAX_UPLOAD_BYTES } from '@designdeck/shared';
/**
 * Application Constants
 *
 * Centralized configuration values to avoid magic numbers throughout the codebase.
 * All timing, size limits, and other configurable values should be defined here.
 */

// =============================================================================
// CANVAS & EDITOR
// =============================================================================

/** Maximum number of undo/redo states to keep in history */
export const MAX_HISTORY_SIZE = 50;

/** Default font size for new text objects */
export const DEFAULT_FONT_SIZE = 32;

/** Minimum font size allowed */
export const MIN_FONT_SIZE = 8;

/** Maximum font size allowed */
export const MAX_FONT_SIZE = 200;

/** Nudge step in pixels (Arrow keys) */
export const NUDGE_STEP_SMALL = 1;

/** Large nudge step in pixels (Shift + Arrow keys) */
export const NUDGE_STEP_LARGE = 10;

// =============================================================================
// FILE UPLOADS
// =============================================================================

/**
 * Maximum file size for uploads. One number for the whole stack, so the
 * dialog never accepts a file the server will reject.
 */
export const MAX_FILE_SIZE = MAX_UPLOAD_BYTES;

/** Allowed image MIME types */
/**
 * What the browser itself can decode and draw. The canvas paths load the
 * chosen file locally before uploading, so anything here must survive
 * `URL.createObjectURL` plus an `<img>`.
 */
export const ALLOWED_IMAGE_TYPES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/webp',
  'image/gif',
  // Rasterised to PNG by the API on arrival
  'image/svg+xml',
] as const;

/**
 * What the API accepts. PDF is here and not above on purpose: no browser
 * decodes it as an image, so it only works where the file goes straight to
 * the server and comes back as the rendered PNG.
 */
export const ALLOWED_UPLOAD_TYPES = [
  ...ALLOWED_IMAGE_TYPES,
  // Single page only; the API renders it to PNG
  'application/pdf',
] as const;

/**
 * What the Direct Sale upload input offers the system picker: the API's
 * types plus the extensions iOS and desktop pickers match files by. AI files
 * are usually PDF-compatible and go up as PDF; EPS is listed so the picker
 * does not hide it and the flow can explain what to do with it.
 */
export const DIRECT_SALE_UPLOAD_ACCEPT_LIST = [
  ...ALLOWED_UPLOAD_TYPES,
  '.pdf',
  '.ai',
  '.eps',
] as const;

/** Allowed file extensions for display */
export const ALLOWED_IMAGE_EXTENSIONS = [
  'PNG',
  'JPG',
  'JPEG',
  'WebP',
  'GIF',
  'SVG',
];

// =============================================================================
// CACHING & TIMING
// =============================================================================

/** URL cache TTL in milliseconds (5 minutes) */
export const URL_CACHE_TTL = 5 * 60 * 1000;

/** Fabric object cache max age in milliseconds (30 minutes) */
export const OBJECT_CACHE_MAX_AGE = 30 * 60 * 1000;

/** Auto-save debounce delay in milliseconds (3 seconds) */
export const AUTO_SAVE_DELAY = 3000;

/** Design preview generation debounce in milliseconds */
export const PREVIEW_DEBOUNCE = 150;

/** History save debounce for nudging in milliseconds */
export const HISTORY_SAVE_DEBOUNCE = 300;

/** Maximum wait time for pending uploads before position switch (10 seconds) */
export const MAX_UPLOAD_WAIT_TIME = 10000;

// =============================================================================
// API & NETWORK
// =============================================================================

/** Maximum retry attempts for uploads */
export const MAX_UPLOAD_RETRIES = 3;

/** Batch size for asset URL resolution */
export const ASSET_URL_BATCH_SIZE = 50;

/** Request timeout in milliseconds */
export const REQUEST_TIMEOUT = 30000;

// =============================================================================
// UI & LAYOUT
// =============================================================================

/** Minimum mockup layer opacity */
export const MIN_LAYER_OPACITY = 0;

/** Maximum mockup layer opacity */
export const MAX_LAYER_OPACITY = 1;

/** Default heathered layer opacity */
export const DEFAULT_HEATHERED_OPACITY = 0.5;

/** Default texture layer opacity */
export const DEFAULT_TEXTURE_OPACITY = 0.4;

/** Layers panel flyout width in pixels */
export const LAYERS_PANEL_WIDTH = 280;

/** Thumbnail size for layer previews in pixels */
export const LAYER_THUMBNAIL_SIZE = 40;

// =============================================================================
// CANVAS SIZING (Responsive)
// =============================================================================

/**
 * Responsive canvas sizing configuration.
 * Used by useCanvasSize hook for fit-with-padding behavior.
 */
export const CANVAS_SIZING = {
  /** Padding around canvas in container (px) */
  CONTAINER_PADDING: 32,

  /** Maximum upscale factor (2 = up to 200% of native size) */
  MAX_SCALE_FACTOR: 2,

  /** Minimum scale factor (prevents too small display) */
  MIN_SCALE_FACTOR: 0.5,
} as const;

// =============================================================================
// CANVAS TOUCH SETTINGS
// =============================================================================

/**
 * Touch optimizations for canvas (like Printify/Gelato).
 * Eliminates 300ms click delay and improves touch interaction.
 */
export const CANVAS_TOUCH_SETTINGS = {
  /** Eliminates 300ms delay on mobile */
  touchAction: 'manipulation',
  /** Prevents text selection during drag */
  userSelect: 'none',
} as const;

// =============================================================================
// VALIDATION
// =============================================================================

/** Minimum design name length */
export const MIN_DESIGN_NAME_LENGTH = 1;

/** Maximum design name length */
export const MAX_DESIGN_NAME_LENGTH = 100;

/** Maximum design description length */
export const MAX_DESIGN_DESCRIPTION_LENGTH = 500;
